import { FC, ReactNode, useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Slider, NumberInput } from './';
import useDebounce from '@/helpers/useDebounce';

const StyledInputSlider = styled.div`
  ${tw`w-full`};
`;

const TopContainer = styled.div`
  ${tw`relative flex items-center w-full h-5 mb-4`};
`;

const StyledNumberInput = styled.div`
  ${tw`mr-2 w-full`};
`;

const Label = styled.label`
  ${tw`text-slate-500 text-xs mr-2`};
`;

interface Props {
  min: number;
  max: number;
  resetValue: boolean;
  value: [number];
  label?: string;
  children?: ReactNode;
  handleValueChange: (value: [number]) => void;
}

const InputSlider: FC<Props> = ({
  min,
  max,
  value,
  label,
  resetValue,
  children,
  handleValueChange,
}) => {
  const [internalValue, setInternalValue] = useState<[number]>(value);
  const debouncedValue = useDebounce(internalValue, 300) as [number];

  // Memoized debounced function to handle value change don't trigger if value is already the same.
  useEffect(() => {
    if (value[0] !== debouncedValue[0]) {
      handleValueChange(debouncedValue);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (value[0] !== internalValue[0]) {
      setInternalValue(value);
    }
  }, [resetValue]);

  useEffect(() => {
    if (value[0] !== internalValue[0]) {
      setInternalValue(value);
    }
  },[value]);

  // Update the internal value and call the debounced handleValueChange
  const handleInputChange = (newValue: [number]) => {
    setInternalValue(newValue);
  };

  return (
    <StyledInputSlider>
      <TopContainer>
        <StyledNumberInput>
          <NumberInput
            min={min}
            max={max}
            handleValueChange={(newValue: number) =>
              handleInputChange([newValue])
            }
            value={internalValue[0]}
          />
        </StyledNumberInput>
        {label && <Label>{label}</Label>}
        {children}
      </TopContainer>
      <Slider
        min={min}
        max={max}
        value={internalValue}
        handleValueChange={handleInputChange}
      />
    </StyledInputSlider>
  );
};

export default InputSlider;
